import { useEffect, useRef, useState } from 'preact/hooks'
import type { ChatView, MessageView } from '#contract'
import { useView } from '../app/use-view.ts'

/** How long a banner stays, in ms. Its CSS animation lasts as long. */
const BANNER_MS = 2000
/** Banners on screen at once; older ones give way. */
const MAX_BANNERS = 3

/** A message shown as a banner under the clock. */
export interface Banner {
  readonly message: MessageView
  readonly chat: ChatView
}

/**
 * The Wassup banners to show: each message that arrives while playing (a higher id than any
 * seen) gets one for `BANNER_MS` while the HUD is `visible`. One that arrives while it's hidden
 * (at the computer, asleep) waits and shows when the HUD is back, unless it was read meanwhile.
 * Messages already there when play starts (a loaded save's) never show. Call it where it stays
 * mounted.
 */
export function useNotifications(playing: boolean, visible: boolean): readonly Banner[] {
  const level = useView((view) => view.level?.id ?? null)
  const chats = useView((view) => view.messages.chats)
  const seenRef = useRef<{ level: string | null; id: number } | null>(null)
  const [pending, setPending] = useState<readonly number[]>([])
  const [shown, setShown] = useState<readonly number[]>([])

  useEffect(() => {
    const newest = Math.max(0, ...chats.flatMap((chat) => chat.messages.map((m) => m.id)))
    if (seenRef.current?.level !== level) {
      seenRef.current = { level, id: newest }
      setPending([])
      setShown([])
      return
    }
    // Loading and the menu come before play: whatever is there by then is old news.
    if (!playing) {
      seenRef.current = { level, id: newest }
      return
    }
    const since = seenRef.current.id
    if (newest <= since) return
    seenRef.current = { level, id: newest }
    const fresh = chats
      .flatMap((chat) => chat.messages.filter((m) => m.id > since).map((m) => m.id))
      .sort((a, b) => a - b)
    setPending((list) => [...list, ...fresh])
  }, [level, chats, playing])

  // Each batch leaves after its own time; clearing `pending` mustn't cancel it.
  const timersRef = useRef(new Set<ReturnType<typeof setTimeout>>())
  useEffect(() => {
    const live = timersRef.current
    return () => {
      for (const timer of live) clearTimeout(timer)
    }
  }, [])
  useEffect(() => {
    if (!visible || pending.length === 0) return
    const ids = pending
    setPending([])
    setShown((list) => [...list, ...ids].slice(-MAX_BANNERS))
    const timer = setTimeout(() => {
      timersRef.current.delete(timer)
      setShown((list) => list.filter((id) => !ids.includes(id)))
    }, BANNER_MS)
    timersRef.current.add(timer)
  }, [visible, pending])

  if (!visible) return []
  return shown.flatMap((id) => {
    const chat = chats.find((c) => c.messages.some((m) => m.id === id))
    const message = chat?.messages.find((m) => m.id === id)
    return chat && message && !message.read ? [{ message, chat }] : []
  })
}
